// Deploy staleness: preact-cli precaches the app shell (including 200.html, which
// names the bundle hashes) and its service worker has no skipWaiting, so a tab open
// across a deploy keeps running the previous build until it is reloaded. That is
// silent -- the old bundle just behaves like the old release.
//
// So: watch for an installed-but-waiting worker, let the caller surface it, and only
// swap once the user accepts. sw.js stays in "waiting" until we post SKIP_WAITING.

// preact-cli only registers a worker in production builds; under `preact watch`
// there is none, so `ready` never settles and the callback simply never fires.
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

export function watchForServiceWorkerUpdate(onUpdateReady: () => void): void {
    if (!("serviceWorker" in navigator)) return;

    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        // Fires once the waiting worker we activated takes control. Guarded because
        // the spec allows more than one controllerchange per swap.
        if (reloading) return;
        reloading = true;
        window.location.reload();
    });

    navigator.serviceWorker.ready
        .then((registration) => {
            // A null controller means this is the first-ever install, not an update --
            // nothing is stale, so don't nag on a user's first visit.
            const isUpdate = () => !!navigator.serviceWorker.controller;

            if (registration.waiting && isUpdate()) onUpdateReady();

            registration.addEventListener("updatefound", () => {
                const incoming = registration.installing;
                if (!incoming) return;
                incoming.addEventListener("statechange", () => {
                    if (incoming.state === "installed" && isUpdate()) onUpdateReady();
                });
            });

            // The browser only re-checks on navigation, and support staff leave this
            // tab open for hours, so poll too.
            const check = () => registration.update().catch(() => undefined);
            setInterval(check, UPDATE_CHECK_INTERVAL_MS);
            check();
        })
        .catch(() => undefined);
}

export function applyServiceWorkerUpdate(): void {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker
        .getRegistration()
        .then((registration) => {
            if (registration?.waiting) registration.waiting.postMessage({ type: "SKIP_WAITING" });
        })
        .catch(() => undefined);
}
